import React, { useEffect, useState } from 'react';
import { Drawer, Form, Button, Table, Tag, Typography, Spin, Empty, message } from 'antd';
import dayjs from 'dayjs';
import api from '../api';
import EmployeeHistoryFields, {
  STATUS_COLOR, historyFormValues, historyPayload,
} from './EmployeeHistoryFields';

const day = (v) => (v ? dayjs(v).format('MM/DD/YYYY') : '—');

// One Employee History entry in a side panel (WFM Skills Assign's Sched Hrs
// link): the entry -- with Employee History Edit (CanEditStaffHistory >= 2)
// its Scheduled Hours, Notes and Training Non-Billable can be changed, the
// rest is read-only (the Employee History page edits those) -- and the
// person's other entries below it.
// onSaved: after a save, so the caller can reload.
export default function EmployeeHistoryDrawer({ open, userId, historyId, name, canEdit, onClose, onSaved }) {
  const [rows, setRows]       = useState([]);
  const [teams, setTeams]     = useState([]);
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    api.get('/api/teams?my_teams=1').then((r) => setTeams(r.data)).catch(() => {});
    api.get('/api/jobs').then((r) => setJobs(r.data)).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !userId) return;
    setLoading(true);
    setRows([]);
    api.get('/api/employee-history', { params: { user_id: userId } })
      .then((r) => setRows(r.data || []))
      .catch(() => message.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [open, userId]);

  const entry = rows.find((r) => r.ID === historyId);
  const others = rows.filter((r) => r.ID !== historyId);

  useEffect(() => {
    if (entry) {
      form.resetFields();
      form.setFieldsValue(historyFormValues(entry));
    }
  }, [entry, form]);

  const save = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await api.put(`/api/employee-history/${historyId}`, historyPayload(values));
      message.success('Record updated');
      onSaved?.();
      onClose();
    } catch {
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // The entry's team is always offered, even when not one of my teams.
  const teamOptions = entry && !teams.some((t) => t.ID === entry.TeamID)
    ? [...teams, { ID: entry.TeamID, Name: entry.TeamName }] : teams;

  return (
    <Drawer
      title={`Employee History: ${name || ''}`}
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={canEdit && entry ? (
        <Button type="primary" loading={saving} onClick={save}>Save</Button>
      ) : null}
    >
      {loading ? <Spin /> : !entry ? <Empty description="Entry not found." /> : (
        <>
          {/* Wording from the user. */}
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            This comes from the Employee History list, which tracks each employee&apos;s start, grad and end date
            with a team + job combination. Employees who move back and forth between teams will have an entry
            each time they move. Transferred In means this is not their first team/job and they transferred into it.
          </Typography.Paragraph>
          <Form form={form} layout="vertical" disabled={!canEdit}>
            <EmployeeHistoryFields
              users={[{ ID: entry.UserID, DisplayName: entry.DisplayName }]}
              teams={teamOptions} jobs={jobs} lockEmployee
              editable={canEdit ? ['ScheduledHours', 'Notes', 'TrainingNonBillable'] : []}
              hoursFirst
            />
          </Form>
          <Typography.Title level={5} style={{ marginTop: 8 }}>Other entries</Typography.Title>
          <Table
            rowKey="ID"
            dataSource={others}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'None.' }}
            columns={[
              { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', render: (v) => v || '—' },
              { title: 'Job', dataIndex: 'JobName', key: 'JobName', render: (v) => v || '—' },
              { title: 'Status', dataIndex: 'Status', key: 'Status',
                render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag> },
              { title: 'Start', dataIndex: 'StartDate', key: 'StartDate', render: day },
              { title: 'End', dataIndex: 'EndDate', key: 'EndDate', render: day },
              { title: 'Hrs', dataIndex: 'ScheduledHours', key: 'ScheduledHours', align: 'right',
                render: (v) => v ?? '—' },
            ]}
          />
        </>
      )}
    </Drawer>
  );
}
